import { PRAYERS, type Building } from '@map/shared';
import { dot, normalise, type Territory } from '@map/worldkit';

/**
 * The prayers, in their order, along the ground.
 *
 * The river runs from Fajr to 'Isha, and a day runs the same way, so the
 * countries should lie in that order rather than wherever the partition
 * happened to drop them — otherwise the water doubles back on itself to keep
 * the order, or keeps its course and tells the day out of sequence.
 *
 * The chain runs north to south, so Fajr is at the top of the continent and
 * 'Isha at the bottom.
 *
 * Nothing moves that anyone has logged: a prayer's country is a cell of the
 * continent, and this only decides which cell each prayer is drawn in. The
 * building, its entries and its rating belong to the prayer and come with it.
 */
export function orderSalahCountries(
  territories: Map<string, Territory>,
  buildings: readonly Building[],
): Map<string, Territory> {
  const byPrayer = new Map(
    buildings.filter((b) => b.kind === 'salah' && b.prayer).map((b) => [b.prayer, b] as const),
  );

  const ids: string[] = [];
  for (const { key } of PRAYERS) {
    const building = byPrayer.get(key);
    const territory = building && territories.get(building.regionId);
    if (!territory) return territories;
    ids.push(territory.id);
  }

  const cells = ids.map((id) => territories.get(id) as Territory);
  // Only a set of countries of the same continent can be shuffled between
  // themselves; anything else and the prayers would swap parents.
  const parent = cells[0]?.parentId;
  if (!parent || cells.some((c) => c.parentId !== parent)) return territories;

  const chain = orient(shortestChain(cells));

  const ordered = new Map(territories);
  ids.forEach((id, i) => {
    const cell = chain[i] as Territory;
    ordered.set(id, { ...cell, id });
  });
  return ordered;
}

/** Angle between two sites, which is distance on a unit sphere. */
function apart(a: Territory, b: Territory): number {
  return Math.acos(Math.min(1, Math.max(-1, dot(normalise(a.site), normalise(b.site)))));
}

/**
 * The shortest path visiting every country once. Five of them is 120 orders,
 * so the answer is the exhaustive one rather than a heuristic that is right
 * most of the time.
 */
function shortestChain(cells: readonly Territory[]): Territory[] {
  let best = [...cells];
  let shortest = Infinity;

  const walk = (done: Territory[], rest: readonly Territory[]) => {
    if (rest.length === 0) {
      let total = 0;
      for (let i = 1; i < done.length; i += 1) {
        total += apart(done[i - 1] as Territory, done[i] as Territory);
      }
      if (total < shortest) {
        shortest = total;
        best = done;
      }
      return;
    }
    rest.forEach((cell, i) => walk([...done, cell], [...rest.slice(0, i), ...rest.slice(i + 1)]));
  };
  walk([], cells);
  return best;
}

/**
 * A chain has no direction of its own, so it is pointed down the planet: Fajr
 * at the northern end, 'Isha at the southern one, and the river running the
 * way the day is read.
 */
function orient(chain: readonly Territory[]): Territory[] {
  const first = chain[0];
  const last = chain[chain.length - 1];
  if (!first || !last) return [...chain];
  return last.site.y > first.site.y ? [...chain].reverse() : [...chain];
}
